/// @ts-check
/**
 * ChatRoom 模式的客製化 Context 構建函數
 * 
 * 策略：
 * 1. 首先基於關聯性（節點往上溯源）
 * 2. 若無上層節點或不足時，則使用時間點向上溯源補充
 * 
 * 架構原則：
 * 1. 此模組負責 ChatRoom 模式特有的 context 構建邏輯
 * 2. 使用共用的類型定義（GroupContext, ConversationHistoryItem）
 * 3. 根據 ChatRoom 的特性客製化處理邏輯
 * 4. 參數明確化：接收標準化的 { nodes, edges } 結構
 */

import { buildUnifiedGroupContext } from '../../../utils/groupContextHelper';
import { traceToRoot } from '../core/CoreForumUtils';
import { 
  extractTitle, 
  extractTimestamp
} from '../../../utils/nodeDataExtractor';
import { getContextBuildParams } from '../../../utils/storageAccessor';
import {
  buildConversationHistoryBatch
} from '../../../utils/conversationHistoryHelper';
import { ConversationHistory, GroupContextValidator } from '../../../types/groupContext.types.js';

/**
 * @typedef {import('../../../types/groupContext.types.js').GroupContext} GroupContext
 * @typedef {import('../../../types/groupContext.types.js').ConversationHistoryItem} ConversationHistoryItem
 * @typedef {import('../../../types/graphData.types.js').GraphData} GraphData
 * @typedef {import('../../../types/graphData.types.js').GraphNode} GraphNode
 * @typedef {import('../../../types/graphData.types.js').GraphEdge} GraphEdge
 */

/**
 * 構建 ChatRoom 模式的小組 context（包含對話歷史）
 * @param {Array<any>} graphNodes - 圖形節點（需包含 _edges 臨時字段）
 * @param {string} currentContent - 當前內容
 * @param {string|number} [tag=''] - 標籤或限制數量（向後兼容）
 * @returns {GroupContext} ChatRoom 專用的完整小組 context
 */
export const buildChatRoomContext = (graphNodes = [], currentContent = '', tag = '') => {
  console.log('[buildChatRoomContext] 輸入參數:', {
    graphNodes
  });
  // 兼容舊調用方式（tag 為數字）
  let limit = 8;
  
  const params = getContextBuildParams();
  const baseContext = buildUnifiedGroupContext(graphNodes, params, 'chat');
  
  // ✅ 從臨時字段提取 edges（與 stickerContext 一致）
  const edges = graphNodes[0]?._edges || [];
  
  console.log('[buildChatRoomContext] 輸入:', {
    nodeCount: graphNodes.length,
    edgeCount: edges.length,
    contentLength: currentContent.length,
    limit
  });
  
  // ✅ 使用 ConversationHistory 類構建對話歷史
  const conversationHistory = getChatRoomConversationHistory(graphNodes, edges, limit);
  
  console.log('[buildChatRoomContext] 輸出:', {
    historyCount: conversationHistory.getCount(),
    participants: [...new Set(conversationHistory.toArray().map(h => h.author))]
  });
  
  // ✅ 使用 GroupContextValidator 創建 context，確保類型正確
  return GroupContextValidator.createWithHistory(
    {
      ...baseContext,
      interactionType: 'real-time-chat',
      expectation: '即時互動和快速回應',
      contextVersion: '3.1-chatroom-standardized'
    },
    conversationHistory
  );
};



/**
 * 獲取 ChatRoom 模式的對話歷史
 * ✅ 使用 ConversationHistory 類，提供類型安全和便捷操作
 * 
 * ChatRoom 策略：
 * 1. 如果有父節點（_currentNodeId），從父節點往上溯源
 * 2. 如果沒有父節點，使用最新節點往上溯源
 * 3. 若溯源節點不足，則使用時間點向上溯源補充
 * 
 * @param {Array<any>} nodes - 所有節點
 * @param {Array<any>} edges - 所有邊
 * @param {number} limit - 對話歷史限制數量
 * @returns {ConversationHistory} ConversationHistory 實例
 */
const getChatRoomConversationHistory = (nodes, edges, limit = 8) => {
  if (!nodes || nodes.length === 0) {
    return ConversationHistory.empty();
  }
  
  // ✅ 優先使用父節點 (_currentNodeId) 作為追溯起點
  const currentNodeId = nodes[0]?._currentNodeId;
  
  let traceFromNodeId;
  if (currentNodeId) {
    // 回覆模式：從父節點追溯
    traceFromNodeId = String(currentNodeId);
    console.log('[ChatRoom] 從父節點追溯:', traceFromNodeId);
  } else {
    // 新 START 節點：使用最新節點
    const sortedNodes = [...nodes].sort((a, b) => {
      const timeA = extractTimestamp(a);
      const timeB = extractTimestamp(b);
      return timeB - timeA; // 降序
    });
    traceFromNodeId = String(sortedNodes[0].id);
    console.log('[ChatRoom] 新 START 節點，使用最新節點:', traceFromNodeId);
  }

  // ✅ 基於關聯性（往上溯源）- 使用 traceToRoot
  const tracedPath = traceToRoot(traceFromNodeId, edges, nodes);
  console.log('[ChatRoom] 溯源結果:', {
    起點: traceFromNodeId,
    ancestorCount: tracedPath.length,
    targetCount: limit
  });

  // traceToRoot 返回的是 node.data.source，需要轉換為完整節點
  let selectedNodes = tracedPath.map(source => {
    return nodes.find(n => String(n.id) === String(source.id)) || 
           { id: source.id, data: { source } };
  });

  // 第二步：如果溯源節點不足，用時間順序補充
  if (selectedNodes.length < limit) {
    const remainingCount = limit - selectedNodes.length;
    const selectedIds = new Set(selectedNodes.map(n => String(n.id)));
    
    const sortedNodes = [...nodes].sort((a, b) => {
      const timeA = extractTimestamp(a);
      const timeB = extractTimestamp(b);
      return timeB - timeA; // 降序
    });
    
    const additionalNodes = sortedNodes
      .filter(node => !selectedIds.has(String(node.id)))
      .slice(0, remainingCount);
    
    selectedNodes = [...selectedNodes, ...additionalNodes];
    
    console.log('[ChatRoom] 時間補充:', {
      補充數量: additionalNodes.length,
      最終總數: selectedNodes.length
    });
  }

  // ✅ 使用模組化批量構建方法（自動驗證）
  const rawHistory = buildConversationHistoryBatch(selectedNodes, {
    useFullText: true,
    validate: true // 啟用類型驗證
  });

  // ✅ 使用 ConversationHistory 類包裝，提供鏈式操作
  const history = ConversationHistory
    .fromArray(rawHistory)
    .deduplicate()           // 去重

  console.log('[ChatRoom] 最終歷史:', {
    選中節點數: selectedNodes.length,
    最終歷史數: history.getCount(),
    參與者: history.getCount() > 0 ? [...new Set(history.toArray().map(h => h.author))] : []
  });

  return history;
};
